<script>
import {
  ensureSelectionInEditor,
  saveSelectionRange,
  createTextFragment,
  moveCaretToEnd,
  setCaretInNode,
  getSelectionRangeInEditor,
  insertFragmentAtRange,
  getEditorText,
  getPlainText,
  formatHeightValue,
  executeToolbarAction,
  handleTableInsertion,
} from '@modules/global/services/markdown-field/markdownFieldService.js'

import {
  MARKDOWN_TOOLBAR_ITEMS,
  TABLE_PICKER_CONFIG,
} from '@modules/global/constants/MarkdownField.js'

import MarkdownFieldDialog from '@modules/global/components/markdown-field-dialog/markdown-field-dialog.vue'
import MarkdownFieldWidgets from '@modules/global/components/markdown-field-widgets/markdown-field-widgets.vue'

export default {
  name: 'MarkdownField',

  components: {
    MarkdownFieldDialog,
    MarkdownFieldWidgets,
  },

  inject: {
    form: {
      default: null,
    },
  },

  inheritAttrs: false,

  props: {
    value: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Digite aqui...',
    },
    label: {
      type: String,
      default: '',
    },
    minHeight: {
      type: [Number, String],
      default: 140,
    },
    maxHeight: {
      type: [Number, String],
      default: 320,
    },
    resizable: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hideWidgets: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array,
      default: () => [],
    },
    editOnlyFullscreen: {
      type: Boolean,
      default: false,
    },
    previewOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      MARKDOWN_TOOLBAR_ITEMS,
      internalValue: this.value || '',
      expandedValue: this.value || '',
      isFocused: false,
      isExpanded: false,
      isToolbarVisible: !this.hideWidgets,
      savedRange: null,
      tablePicker: {
        rows: TABLE_PICKER_CONFIG.MAX_ROWS,
        cols: TABLE_PICKER_CONFIG.MAX_COLS,
        hoverRows: 0,
        hoverCols: 0,
        openMain: false,
        openExpanded: false,
      },
      hasError: false,
      errorMessages: [],
    }
  },

  computed: {
    canEdit() {
      return !this.disabled && !this.previewOnly
    },

    hideInlineEditor() {
      return this.editOnlyFullscreen || this.previewOnly
    },

    activeEditor() {
      if (this.isExpanded) {
        return this.getExpandedEditor()
      }
      return this.$refs.editor
    },

    markdownFieldClass() {
      return {
        'aawz-markdown-field': true,
        'is-disabled': this.disabled,
        'toolbar-hidden': !this.isToolbarVisible,
        'has-error': this.hasError,
      }
    },

    tablePickerLabel() {
      const rows = this.tablePicker.hoverRows
      const cols = this.tablePicker.hoverCols
      if (!rows || !cols) {
        return 'Selecione o tamanho'
      }
      return cols + ' x ' + rows
    },

    editorStyle() {
      return {
        minHeight: formatHeightValue(this.minHeight),
        maxHeight: formatHeightValue(this.maxHeight),
      }
    },

    editorClass() {
      return {
        'has-placeholder': !this.internalValue,
        'is-resizable': this.resizable,
        'is-markdown': true,
      }
    },
  },

  watch: {
    value(nextValue) {
      this.handleValueChange(nextValue)
    },

    hideWidgets(nextValue) {
      this.isToolbarVisible = !nextValue
    },

    isExpanded(nextValue) {
      this.handleExpandedChange(nextValue)
    },
  },

  mounted() {
    this.syncEditorText()
    document.addEventListener('selectionchange', this.onSelectionChange)

    if (this.form) {
      this.form.register(this)
    }
  },

  beforeDestroy() {
    document.removeEventListener('selectionchange', this.onSelectionChange)

    if (this.form) {
      this.form.unregister(this)
    }
  },

  methods: {
    // ============ STATE MANAGEMENT ============

    getExpandedEditor() {
      const dialog = this.$refs.expandedDialog
      if (!dialog) {
        return null
      }
      if (typeof dialog.getEditor !== 'function') {
        return null
      }
      return dialog.getEditor()
    },

    // ============ EDITOR SYNCHRONIZATION ============

    syncEditorText() {
      const editor = this.$refs.editor
      if (!editor) {
        return
      }

      const text = this.internalValue || ''
      if (editor.innerText !== text) {
        editor.innerText = text
      }
    },

    syncExpandedText() {
      const editor = this.getExpandedEditor()
      if (!editor) {
        return
      }

      const text = this.expandedValue || ''
      if (editor.innerText !== text) {
        editor.innerText = text
      }
    },

    syncInactiveEditors() {
      const text = this.internalValue || ''
      const mainEditor = this.$refs.editor
      const expandedEditor = this.getExpandedEditor()

      if (mainEditor && this.activeEditor !== mainEditor) {
        if (mainEditor.innerText !== text) {
          mainEditor.innerText = text
        }
      }

      if (expandedEditor && this.activeEditor !== expandedEditor) {
        if (expandedEditor.innerText !== text) {
          expandedEditor.innerText = text
        }
      }
    },

    handleValueChange(nextValue) {
      const newValue = nextValue || ''
      if (newValue !== this.internalValue) {
        this.internalValue = newValue
        this.expandedValue = newValue
        this.syncEditorText()
        this.syncExpandedText()
      }
    },

    handleExpandedChange(isExpanded) {
      this.$nextTick(() => {
        if (isExpanded) {
          this.expandToFullScreen()
        } else {
          this.collapseFromFullScreen()
        }
        this.focusEditor()
      })
    },

    expandToFullScreen() {
      this.expandedValue = this.internalValue
      this.syncExpandedText()
    },

    collapseFromFullScreen() {
      this.internalValue = this.expandedValue
      this.syncEditorText()
      this.emitValue(this.internalValue)
    },

    // ============ SELECTION MANAGEMENT ============

    ensureSelectionInEditor() {
      ensureSelectionInEditor({
        deps: {
          editor: this.activeEditor,
          document: document,
          window: window,
        },
      })
    },

    saveSelectionRange() {
      const nextRange = saveSelectionRange({
        deps: {
          editor: this.activeEditor,
          window: window,
        },
      })
      if (nextRange) {
        this.savedRange = nextRange
      }
      return nextRange
    },

    // ============ VALUE MANAGEMENT ============

    emitValue(value) {
      this.$emit('input', value)
      this.$emit('change', value)
    },

    focusEditor() {
      const editor = this.activeEditor
      if (editor) {
        editor.focus()
      }
      this.ensureSelectionInEditor()
    },

    onInput() {
      const editor = this.activeEditor
      if (!editor) {
        return
      }

      const nextValue = getEditorText(editor)
      const currentValue = this.getCurrentValue()

      if (nextValue !== currentValue) {
        this.updateEditorValue(nextValue)
      }

      this.validateIfNeeded()
    },

    getCurrentValue() {
      if (this.isExpanded) {
        return this.expandedValue
      }
      return this.internalValue
    },

    updateEditorValue(value) {
      if (this.isExpanded) {
        this.expandedValue = value
        return
      }

      this.internalValue = value
      this.emitValue(this.internalValue)
      this.syncInactiveEditors()
    },

    // ============ EVENT HANDLERS ============

    onFocus() {
      this.isFocused = true
    },

    onBlur() {
      this.isFocused = false
    },

    onSelectionChange() {
      this.saveSelectionRange()
    },

    onPaste(event) {
      if (!this.canEdit) {
        return
      }

      const clipboard = event.clipboardData
      if (!clipboard) {
        return
      }

      const text = clipboard.getData('text/plain')
      if (!text) {
        return
      }

      event.preventDefault()
      this.focusEditor()
      this.insertTextAtSelection(text)
      this.onInput()
    },

    // ============ TOOLBAR ACTIONS ============

    onToolbarAction(item) {
      if (!item || !this.canEdit) {
        return
      }

      this.focusEditor()

      const range = getSelectionRangeInEditor(this.activeEditor)
      if (!range) {
        return
      }

      executeToolbarAction({
        action: item.action,
        range: range,
        insertText: (text) => this.insertTextAtSelection(text),
        setCaretPosition: (node, offset) => setCaretInNode(node, offset),
      })

      this.onInput()
    },

    // ============ TEXT INSERTION ============

    insertTextAtSelection(text) {
      const range = getSelectionRangeInEditor(this.activeEditor)
      if (!range) {
        return
      }

      if (document && typeof document.execCommand === 'function') {
        try {
          const inserted = document.execCommand('insertText', false, text)
          if (inserted) {
            return
          }
        } catch (error) {
          // fallback to manual insertion
        }
      }

      const fragment = createTextFragment(text)
      const lastNode = insertFragmentAtRange(range, fragment)

      if (lastNode) {
        moveCaretToEnd(lastNode)
      }
    },

    // ============ TABLE PICKER MANAGEMENT ============

    setTablePickerHover(row, col) {
      this.tablePicker.hoverRows = row
      this.tablePicker.hoverCols = col
    },

    clearTablePickerHover() {
      this.tablePicker.hoverRows = 0
      this.tablePicker.hoverCols = 0
    },

    applyTableFromPicker(row, col) {
      this.focusEditor()
      handleTableInsertion(row, col, (text) => this.insertTextAtSelection(text))
      this.tablePicker.openMain = false
      this.tablePicker.openExpanded = false
      this.clearTablePickerHover()
      this.onInput()
    },

    // ============ UI ACTIONS ============

    toggleToolbar() {
      this.isToolbarVisible = !this.isToolbarVisible
    },

    openExpanded() {
      if (this.isExpanded) {
        return
      }
      this.isExpanded = true
    },

    closeExpanded() {
      if (!this.isExpanded) {
        return
      }
      this.isExpanded = false
    },

    handleDialogSave(text) {
      const value = text !== undefined ? text : this.expandedValue
      this.internalValue = value
      this.expandedValue = value
      this.syncEditorText()
      this.emitValue(this.internalValue)
      this.$emit('save', this.internalValue)
    },

    // ============ VALIDATION ============

    validateIfNeeded() {
      if (this.hasError) {
        this.validate()
      }
    },

    validate() {
      this.errorMessages = []
      this.hasError = false

      const value = getPlainText(this.internalValue)

      for (const rule of this.rules) {
        const result = typeof rule === 'function' ? rule(value) : true

        if (result !== true) {
          this.hasError = true
          this.errorMessages.push(result)
          break
        }
      }

      return !this.hasError
    },

    resetValidation() {
      this.hasError = false
      this.errorMessages = []
    },
  },
}
</script>

<template>
  <div :class="markdownFieldClass">
    <div v-if="label && !hideInlineEditor" class="az-text-label">{{
      label
    }}</div>
    <div
      v-if="isToolbarVisible && !hideInlineEditor"
      class="aawz-markdown-field__toolbar"
    >
      <MarkdownFieldWidgets
        :can-edit="canEdit"
        :table-picker="tablePicker"
        :table-picker-label="tablePickerLabel"
        :menu-value="tablePicker.openMain"
        @toolbar-action="onToolbarAction"
        @table-open="tablePicker.openMain = $event"
        @table-hover="setTablePickerHover"
        @table-clear="clearTablePickerHover"
        @table-apply="applyTableFromPicker"
      />

      <div class="d-flex align-center az-gap-xsmall">
        <v-btn
          class="btn-icon-square is-active"
          icon
          x-small
          :disabled="!canEdit"
          title="Opções de formatação"
          @mousedown.prevent
          @click="toggleToolbar"
        >
          <icon-svg name="text-formatter" size="18" />
        </v-btn>

        <v-btn
          class="btn-icon-square"
          icon
          x-small
          :disabled="!canEdit"
          title="Expandir editor"
          @mousedown.prevent
          @click="openExpanded"
        >
          <v-icon>mdi-fullscreen</v-icon>
        </v-btn>
      </div>
    </div>

    <div v-if="!hideInlineEditor" class="aawz-markdown-field__editor-wrapper">
      <v-btn
        v-if="!isToolbarVisible"
        class="btn-icon-square aawz-markdown-field__toolbar-toggle--floating"
        icon
        x-small
        :disabled="!canEdit"
        title="Opções de formatação"
        @mousedown.prevent
        @click="toggleToolbar"
      >
        <icon-svg name="text-formatter" size="18" />
      </v-btn>

      <v-btn
        v-if="!isToolbarVisible"
        class="btn-icon-square aawz-markdown-field__toolbar-toggle--expand"
        icon
        x-small
        :disabled="!canEdit"
        title="Expandir editor"
        @mousedown.prevent
        @click="openExpanded"
      >
        <v-icon>mdi-fullscreen</v-icon>
      </v-btn>

      <div
        ref="editor"
        class="aawz-markdown-field__editor new-ux-scroll"
        :class="editorClass"
        :contenteditable="canEdit"
        :data-placeholder="placeholder"
        :style="editorStyle"
        :aria-disabled="!canEdit"
        v-bind="$attrs"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @mouseup="onSelectionChange"
        @keyup="onSelectionChange"
        @paste="onPaste"
      >
      </div>

      <div v-if="$slots.footer" class="aawz-markdown-field__footer">
        <slot name="footer" />
      </div>

      <v-slide-y-transition>
        <div v-if="hasError" class="aawz-markdown-field__error">
          {{ errorMessages[0] }}
        </div>
      </v-slide-y-transition>
    </div>

    <MarkdownFieldDialog
      ref="expandedDialog"
      v-model="isExpanded"
      :can-edit="canEdit"
      :preview-only="previewOnly"
      :table-picker="tablePicker"
      :table-picker-label="tablePickerLabel"
      :placeholder="placeholder"
      :is-empty="!expandedValue"
      :preview-value="expandedValue"
      v-bind="$attrs"
      @toolbar-action="onToolbarAction"
      @table-open-expanded="tablePicker.openExpanded = $event"
      @table-hover="setTablePickerHover"
      @table-clear="clearTablePickerHover"
      @table-apply="applyTableFromPicker"
      @editor-input="onInput"
      @editor-focus="onFocus"
      @editor-blur="onBlur"
      @editor-mouseup="onSelectionChange"
      @editor-keyup="onSelectionChange"
      @editor-paste="onPaste"
      @close="closeExpanded"
      @save="handleDialogSave"
    />
  </div>
</template>

<style lang="sass">
.aawz-markdown-field
  display: flex
  flex-direction: column
  gap: 0

  &.is-disabled
    .aawz-markdown-field__toolbar
      opacity: 0.6

  &.toolbar-hidden
    .aawz-markdown-field__editor
      border-radius: 8px
      border-top: 1px solid #E2E3E4

  &:hover
    .aawz-markdown-field__toolbar,
    .aawz-markdown-field__editor
      border-color: rgba(0, 0, 0, 0.8)

  &:focus-within
    .aawz-markdown-field__toolbar,
    .aawz-markdown-field__editor
      border-color: var(--v-primary-base)

  &.has-error
    .aawz-markdown-field__toolbar
      border-color: var(--v-error-base) !important

    .aawz-markdown-field__editor
      border-color: var(--v-error-base) !important

    &:hover
      .aawz-markdown-field__toolbar,
      .aawz-markdown-field__editor
        border-color: var(--v-error-base) !important

    &:focus-within
      .aawz-markdown-field__toolbar,
      .aawz-markdown-field__editor
        border-color: var(--v-error-base) !important

.aawz-markdown-field__label
  color: #5B5B5B
  font-weight: 500

.aawz-markdown-field__toolbar
  display: flex
  align-items: center
  justify-content: space-between
  flex-wrap: nowrap
  gap: 4px
  padding: 6px 8px
  background: #F6F7F9
  border: 1px solid #E2E3E4
  border-bottom: 0
  border-radius: 8px 8px 0 0
  transition: border-color 0.2s ease

  .btn-icon-square
    &.is-active
      background: #8E8E8E !important
      color: #fff !important

.aawz-markdown-field__toolbar-tools
  display: flex
  align-items: center
  flex-wrap: wrap
  gap: 4px

.aawz-markdown-field__toolbar-toggle--floating
  position: absolute
  top: 4px
  right: 36px
  z-index: 1

.aawz-markdown-field__toolbar-toggle--expand
  position: absolute
  top: 4px
  right: 8px
  z-index: 1

.aawz-markdown-field__table-picker
  padding: 8px
  background: #FFFFFF

.aawz-markdown-field__table-grid
  display: grid
  grid-template-columns: repeat(10, 16px)
  gap: 4px

.aawz-markdown-field__table-cell
  width: 16px
  height: 16px
  border: 1px solid #D7D9DC
  background: #FFFFFF

  &.is-selected
    border-color: #4C8BF5
    background: #E8F0FE

.aawz-markdown-field__table-label
  margin-top: 6px
  font-size: 12px
  color: #5E5E5E

.aawz-markdown-field__divider
  width: 1px
  height: 24px
  background: #D7D9DC
  margin: 0 4px

.aawz-markdown-field__editor-wrapper
  position: relative

.aawz-markdown-field__editor
  border: 1px solid #E2E3E4
  border-radius: 0 0 8px 8px
  border-top: 0
  padding: 20px 12px 18px 12px
  background: #FFFFFF
  outline: none
  font-size: 14px
  color: #2C2C2C
  overflow-y: auto
  transition: border-color 0.2s ease, box-shadow 0.2s ease

  &:hover
    border-color: rgba(0, 0, 0, 0.8)

  &:focus
    border-color: var(--v-primary-base)

  &.has-placeholder:empty:before
    content: attr(data-placeholder)
    color: #9AA0A6

  &.new-ux-scroll
    &::-webkit-scrollbar
      width: 4px !important
      height: 4px !important

  &.is-resizable
    resize: vertical
    overflow: auto
    padding-bottom: 26px
    padding-right: 20px

  &.is-markdown
    font-family: 'Courier New', Courier, monospace
    white-space: pre-wrap

.aawz-markdown-field__footer
  position: absolute
  bottom: 0
  left: 0
  right: 0
  width: 100%
  padding: 12px
  pointer-events: none

  > *
    pointer-events: auto

.aawz-markdown-field__error
  position: absolute
  bottom: 8px
  left: 8px
  font-size: 12px
  color: var(--v-error-base)
</style>
